// Workshop color utilities, shared by the backoffice and (mirrored in Dart,
// apps/inspeccion/lib/tema/color_taller.dart) the inspection app. Pure and
// dependency-free: both implementations must return the very same hex for
// the vectors in color.vectores.json.
//
// - Contrast is WCAG 2.x (relative luminance, (L1 + 0.05) / (L2 + 0.05)).
// - Lightening/darkening happens in OKLCH: only the lightness moves, so the
//   hue is kept (HSL lightness is not perceptual and shifts blues/yellows).
//   When a lightness has no in-gamut color with the original chroma, the
//   chroma is reduced (bisection) until it fits sRGB.
// - Every search tests the ROUNDED hex, so a returned color always meets its
//   minimum exactly as it will be painted.

/** `#RRGGBB` (the only format stored in config/general.marca.color). */
export const HEX_COLOR = /^#[0-9A-Fa-f]{6}$/;

export type Rgb = { r: number; g: number; b: number };
export type Oklch = { l: number; c: number; h: number };

/** Minimum contrasts guaranteed by variantesTema (WCAG 2.x AA). */
export const CONTRASTE_TEXTO = 4.5;
/** Non-text elements (focus rings, accents, icons): WCAG 1.4.11. */
export const CONTRASTE_NO_TEXTO = 3;

/** Tallerza surfaces the workshop color is painted on (packages/tokens). */
export const SUPERFICIES = {
  claro: { bg: '#FBF9F5', surface: '#FFFFFF' },
  oscuro: { bg: '#161413', surface: '#221F1E' },
} as const;

/** Share of the workshop color in the soft tint (the rest is the surface). */
const MEZCLA_SOFT = { claro: 0.12, oscuro: 0.18 } as const;

export const esHexColor = (s: unknown): s is string => typeof s === 'string' && HEX_COLOR.test(s);

export function hexARgb(hex: string): Rgb {
  if (!esHexColor(hex)) throw new Error(`Color inválido: ${String(hex)} (se espera #RRGGBB)`);
  const n = Number.parseInt(hex.slice(1), 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

const canal = (x: number) => Math.min(255, Math.max(0, Math.round(x)));
const hex2 = (x: number) => canal(x).toString(16).padStart(2, '0');

/** Channels are rounded and clamped to 0..255; output is uppercase. */
export const rgbAHex = ({ r, g, b }: Rgb): string =>
  `#${hex2(r)}${hex2(g)}${hex2(b)}`.toUpperCase();

/** sRGB channel 0..255 → linear 0..1 (the IEC 61966-2-1 threshold). */
const lineal = (c: number) => {
  const x = c / 255;
  return x <= 0.04045 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
};
const gamma = (x: number) =>
  255 * (x <= 0.0031308 ? 12.92 * x : 1.055 * Math.pow(x, 1 / 2.4) - 0.055);

/** WCAG 2.x relative luminance, 0 (black) .. 1 (white). */
export function luminancia(hex: string): number {
  const { r, g, b } = hexARgb(hex);
  return 0.2126 * lineal(r) + 0.7152 * lineal(g) + 0.0722 * lineal(b);
}

/** WCAG 2.x contrast ratio, 1..21, symmetric. */
export function contraste(a: string, b: string): number {
  const la = luminancia(a);
  const lb = luminancia(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

// ---- OKLab / OKLCH (Björn Ottosson, 2020) ----

// Cube root through pow so the Dart mirror (no cbrt in dart:math) matches.
const raizCubica = (x: number) => (x < 0 ? -Math.pow(-x, 1 / 3) : Math.pow(x, 1 / 3));

type Lab = { l: number; a: number; b: number };

function rgbAOklab({ r, g, b }: Rgb): Lab {
  const lr = lineal(r);
  const lg = lineal(g);
  const lb = lineal(b);
  const l = raizCubica(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb);
  const m = raizCubica(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb);
  const s = raizCubica(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb);
  return {
    l: 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    a: 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    b: 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  };
}

/** OKLab → LINEAR sRGB (may fall outside 0..1: out of gamut). */
function oklabALineal({ l: L, a, b }: Lab): [number, number, number] {
  const l = Math.pow(L + 0.3963377774 * a + 0.2158037573 * b, 3);
  const m = Math.pow(L - 0.1055613458 * a - 0.0638541728 * b, 3);
  const s = Math.pow(L - 0.0894841775 * a - 1.291485548 * b, 3);
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
}

export function hexAOklch(hex: string): Oklch {
  const { l, a, b } = rgbAOklab(hexARgb(hex));
  const c = Math.sqrt(a * a + b * b);
  const h = c < 1e-9 ? 0 : ((Math.atan2(b, a) * 180) / Math.PI + 360) % 360;
  return { l, c, h };
}

const EPS_GAMUT = 1e-7;
const enGamut = (x: [number, number, number]) =>
  x.every((v) => v >= -EPS_GAMUT && v <= 1 + EPS_GAMUT);

function lchALineal(l: number, c: number, h: number): [number, number, number] {
  const rad = (h * Math.PI) / 180;
  return oklabALineal({ l, a: c * Math.cos(rad), b: c * Math.sin(rad) });
}

/** OKLCH → hex, reducing chroma (hue and lightness kept) until it fits sRGB. */
export function oklchAHex({ l, c, h }: Oklch): string {
  const L = Math.min(1, Math.max(0, l));
  let lin = lchALineal(L, c, h);
  if (!enGamut(lin)) {
    let lo = 0;
    let hi = c;
    for (let i = 0; i < 24; i++) {
      const mid = (lo + hi) / 2;
      if (enGamut(lchALineal(L, mid, h))) lo = mid;
      else hi = mid;
    }
    lin = lchALineal(L, lo, h);
  }
  const aCanal = (v: number) => gamma(Math.min(1, Math.max(0, v)));
  return rgbAHex({ r: aCanal(lin[0]), g: aCanal(lin[1]), b: aCanal(lin[2]) });
}

/** Mixes `color` into `base`: peso 0 = base, 1 = color (sRGB, per channel). */
export function mezclar(color: string, base: string, peso: number): string {
  const a = hexARgb(color);
  const b = hexARgb(base);
  return rgbAHex({
    r: b.r + (a.r - b.r) * peso,
    g: b.g + (a.g - b.g) * peso,
    b: b.b + (a.b - b.b) * peso,
  });
}

export type Direccion = 'oscurecer' | 'aclarar';

const cumple = (hex: string, fondos: readonly string[], minimo: number) =>
  fondos.every((f) => contraste(hex, f) >= minimo);

/**
 * The closest color to `hex` (same OKLCH hue, only lightness moved in
 * `direccion`) with contrast ≥ `minimo` against every one of `fondos`.
 * Returns `hex` itself (normalized to uppercase) when it already meets it.
 */
export function ajustarContraste(
  hex: string,
  fondos: readonly string[],
  minimo: number,
  direccion: Direccion,
): string {
  const base = hex.toUpperCase();
  if (cumple(base, fondos, minimo)) return base;
  const { l, c, h } = hexAOklch(base);
  // Bisection over lightness: `ok` always meets (starts at black/white), `mal` never.
  let ok = direccion === 'oscurecer' ? 0 : 1;
  let mal = l;
  let mejor = oklchAHex({ l: ok, c, h });
  for (let i = 0; i < 32; i++) {
    const mid = (ok + mal) / 2;
    const candidato = oklchAHex({ l: mid, c, h });
    if (cumple(candidato, fondos, minimo)) {
      ok = mid;
      mejor = candidato;
    } else mal = mid;
  }
  return mejor;
}

/** The two workshop colors (config/general.marca.interfaz). */
export type ColoresTaller = {
  /** Action: buttons, links, active nav item, progress bars. */
  primario: string;
  /** Brand: receipt titles and rules, decorative details. */
  acento: string;
};

export type VarianteTema = {
  /** Filled action (buttons, selected tab): `onPrimary` on it ≥ 4.5:1. */
  primary: string;
  /** Links and colored text: ≥ 4.5:1 on the mode's surfaces and primarySoft. */
  primaryText: string;
  /** Tinted background (active nav item). */
  primarySoft: string;
  /** Text/icons on `primary`. */
  onPrimary: string;
  /** Non-text accent (rules, rings, decorative): ≥ 3:1 on the mode's surfaces. */
  acento: string;
  /** The accent used as text (titles): ≥ 4.5:1 on the mode's surfaces. */
  acentoTexto: string;
};
export type VariantesTema = { claro: VarianteTema; oscuro: VarianteTema };

/**
 * Theme variants of the workshop colors that always meet contrast. A single
 * hex means primario = acento.
 *
 * Light (surface #FFFFFF, bg #FBF9F5):
 *  - primary: primario darkened until #FFFFFF on it ≥ 4.5:1; onPrimary #FFFFFF.
 *  - primarySoft: 12 % of primary over #FFFFFF (the Tallerza tint ratio).
 *  - primaryText: primario darkened until ≥ 4.5:1 on #FFFFFF, #FBF9F5 and
 *    primarySoft.
 *  - acento / acentoTexto: acento darkened the least for ≥ 3:1 / ≥ 4.5:1 on
 *    #FFFFFF and #FBF9F5.
 * Dark (bg #161413, surface #221F1E):
 *  - primary: primario lightened until ≥ 4.5:1 on #161413; onPrimary
 *    #161413 (the same pair, so dark text on it also meets 4.5:1).
 *  - primarySoft: 18 % of primary over #161413.
 *  - primaryText: primario lightened until ≥ 4.5:1 on #161413, #221F1E and
 *    primarySoft.
 *  - acento / acentoTexto: acento lightened the least for ≥ 3:1 / ≥ 4.5:1
 *    on #161413 and #221F1E.
 *
 * The soft tint is fixed first and the text adjusted against it, so the tint
 * stays visible even when the color barely meets 4.5:1 on white.
 */
export function variantesTema(colores: string | ColoresTaller): VariantesTema {
  const { primario, acento } =
    typeof colores === 'string' ? { primario: colores, acento: colores } : colores;
  hexARgb(primario); // throw on anything but #RRGGBB
  hexARgb(acento);
  const p = primario.toUpperCase();
  const a = acento.toUpperCase();
  const cl = SUPERFICIES.claro;
  const os = SUPERFICIES.oscuro;
  const fondosClaro = [cl.surface, cl.bg];
  const fondosOscuro = [os.bg, os.surface];

  const primaryClaro = ajustarContraste(p, [cl.surface], CONTRASTE_TEXTO, 'oscurecer');
  const softClaro = mezclar(primaryClaro, cl.surface, MEZCLA_SOFT.claro);
  const primaryOscuro = ajustarContraste(p, [os.bg], CONTRASTE_TEXTO, 'aclarar');
  const softOscuro = mezclar(primaryOscuro, os.bg, MEZCLA_SOFT.oscuro);

  return {
    claro: {
      primary: primaryClaro,
      primaryText: ajustarContraste(p, [...fondosClaro, softClaro], CONTRASTE_TEXTO, 'oscurecer'),
      primarySoft: softClaro,
      onPrimary: '#FFFFFF',
      acento: ajustarContraste(a, fondosClaro, CONTRASTE_NO_TEXTO, 'oscurecer'),
      acentoTexto: ajustarContraste(a, fondosClaro, CONTRASTE_TEXTO, 'oscurecer'),
    },
    oscuro: {
      primary: primaryOscuro,
      primaryText: ajustarContraste(p, [...fondosOscuro, softOscuro], CONTRASTE_TEXTO, 'aclarar'),
      primarySoft: softOscuro,
      onPrimary: os.bg,
      acento: ajustarContraste(a, fondosOscuro, CONTRASTE_NO_TEXTO, 'aclarar'),
      acentoTexto: ajustarContraste(a, fondosOscuro, CONTRASTE_TEXTO, 'aclarar'),
    },
  };
}

/**
 * The receipt/document color (config/general.marca.color) for a primario:
 * the primario darkened, if needed, to ≥ 4.5:1 on white paper. The PDF
 * paints it as small text and thin rules on white, so the primario (the
 * readable color by construction) wins over the accent, which only
 * guarantees 3:1.
 */
export const colorDocumento = (primario: string): string =>
  ajustarContraste(primario, ['#FFFFFF'], CONTRASTE_TEXTO, 'oscurecer');

// ---- Palette extraction (logo → up to 6 colors) ----

export type OpcionesPaleta = {
  /** Maximum colors returned (default 6). */
  maximo?: number;
  /** Drop near-black clusters (default true: outlines and text of logos). */
  descartarNegros?: boolean;
  /** Minimum share of the opaque pixels for a cluster to count (default 0.01). */
  pesoMinimo?: number;
};

/** Pixels with alpha under this are transparent (logo background). */
export const ALFA_MINIMO = 128;
/** OKLab lightness over this is near-white (paper background). */
export const L_CASI_BLANCO = 0.93;
/** OKLab lightness under this is near-black. */
export const L_CASI_NEGRO = 0.2;
/** OKLCH chroma under this is near-gray. */
export const C_CASI_GRIS = 0.04;
/** OKLab distance under which two buckets are the same color. */
const DISTANCIA_GRUPO = 0.08;

type Grupo = { n: number; r: number; g: number; b: number; lab: Lab };

/**
 * Up to `maximo` `#RRGGBB` colors of an RGBA pixel buffer (canvas
 * getImageData), most relevant first. Relevance = share of pixels × OKLCH
 * chroma, so a big gray area loses to a small, vivid brand color.
 *
 * Deterministic: pixels go into 32-level RGB buckets (5 bits per channel);
 * buckets are visited by count (desc, then key asc) and greedily joined to
 * the first group within DISTANCIA_GRUPO in OKLab, else they open one. A
 * group's color is the count-weighted mean of its pixels (sRGB, rounded).
 * Transparent (alpha < 128), near-white, near-gray and, optionally,
 * near-black groups are dropped.
 */
export function extraerPaleta(pixeles: Uint8ClampedArray, opciones: OpcionesPaleta = {}): string[] {
  const maximo = opciones.maximo ?? 6;
  const descartarNegros = opciones.descartarNegros ?? true;
  const pesoMinimo = opciones.pesoMinimo ?? 0.01;

  const cubos = new Map<number, { n: number; r: number; g: number; b: number }>();
  let opacos = 0;
  for (let i = 0; i + 3 < pixeles.length; i += 4) {
    // Typed-array reads past the end are undefined; the loop bound avoids them.
    if ((pixeles[i + 3] ?? 0) < ALFA_MINIMO) continue;
    const r = pixeles[i] ?? 0;
    const g = pixeles[i + 1] ?? 0;
    const b = pixeles[i + 2] ?? 0;
    opacos++;
    const clave = ((r >> 3) << 10) | ((g >> 3) << 5) | (b >> 3);
    const cubo = cubos.get(clave);
    if (cubo) {
      cubo.n++;
      cubo.r += r;
      cubo.g += g;
      cubo.b += b;
    } else cubos.set(clave, { n: 1, r, g, b });
  }
  if (opacos === 0) return [];

  const orden = [...cubos.entries()].sort((x, y) => y[1].n - x[1].n || x[0] - y[0]);
  const grupos: Grupo[] = [];
  for (const [, c] of orden) {
    const lab = rgbAOklab({ r: c.r / c.n, g: c.g / c.n, b: c.b / c.n });
    const grupo = grupos.find(
      (x) => Math.hypot(x.lab.l - lab.l, x.lab.a - lab.a, x.lab.b - lab.b) < DISTANCIA_GRUPO,
    );
    if (grupo) {
      grupo.n += c.n;
      grupo.r += c.r;
      grupo.g += c.g;
      grupo.b += c.b;
    } else grupos.push({ n: c.n, r: c.r, g: c.g, b: c.b, lab });
  }

  const candidatos: { hex: string; relevancia: number }[] = [];
  for (const x of grupos) {
    const peso = x.n / opacos;
    if (peso < pesoMinimo) continue;
    const hex = rgbAHex({ r: x.r / x.n, g: x.g / x.n, b: x.b / x.n });
    const { l, c } = hexAOklch(hex);
    if (l > L_CASI_BLANCO || c < C_CASI_GRIS) continue;
    if (descartarNegros && l < L_CASI_NEGRO) continue;
    candidatos.push({ hex, relevancia: peso * c });
  }
  candidatos.sort(
    (x, y) => y.relevancia - x.relevancia || (x.hex < y.hex ? -1 : x.hex > y.hex ? 1 : 0),
  );
  const unicos = [...new Set(candidatos.map((x) => x.hex))];
  return unicos.slice(0, maximo);
}

/** Smallest angle between two hues, in degrees. */
const distanciaTono = (h1: number, h2: number) => {
  const d = Math.abs(h1 - h2) % 360;
  return d > 180 ? 360 - d : d;
};
/** Accents closer in hue than this to the primario are "the same color". */
export const TONO_DISTINTO = 30;

function elegirPrimario(paleta: readonly string[]): { fuente: string; color: string } | null {
  const validos = paleta.filter(esHexColor).map((x) => x.toUpperCase());
  const legible = validos.find((x) => contraste(x, '#FFFFFF') >= CONTRASTE_TEXTO);
  if (legible) return { fuente: legible, color: legible };
  const [fuente] = validos;
  if (fuente === undefined) return null;
  return { fuente, color: ajustarContraste(fuente, ['#FFFFFF'], CONTRASTE_TEXTO, 'oscurecer') };
}

/**
 * Suggested workshop primario from an extracted palette (most relevant
 * first): the first color that already has ≥ 4.5:1 on white; if none does,
 * the first one darkened (same hue) until it does. Invalid entries are
 * ignored; an empty palette gives null.
 */
export function sugerirAcento(paleta: readonly string[]): string | null {
  return elegirPrimario(paleta)?.color ?? null;
}

export type TemaSugerido = ColoresTaller & {
  /** The accent darkened to ≥ 4.5:1 on white, for text (receipt titles). */
  acentoTexto: string;
};

/**
 * The "logo" color mode: both workshop colors from an extracted palette.
 *  - primario: sugerirAcento (≥ 4.5:1 with white).
 *  - acento: the next most relevant palette color whose OKLCH hue is at
 *    least TONO_DISTINTO degrees from the primario's source; without one,
 *    the primario's source color itself. Adjusted the least for ≥ 3:1 on
 *    white (acentoTexto: ≥ 4.5:1).
 * Null for an empty palette.
 */
export function temaDesdePaleta(paleta: readonly string[]): TemaSugerido | null {
  const elegido = elegirPrimario(paleta);
  if (!elegido) return null;
  const tonoPrimario = hexAOklch(elegido.fuente).h;
  const otro = paleta
    .filter(esHexColor)
    .map((x) => x.toUpperCase())
    .find(
      (x) => x !== elegido.fuente && distanciaTono(hexAOklch(x).h, tonoPrimario) >= TONO_DISTINTO,
    );
  const base = otro ?? elegido.fuente;
  return {
    primario: elegido.color,
    acento: ajustarContraste(base, ['#FFFFFF'], CONTRASTE_NO_TEXTO, 'oscurecer'),
    acentoTexto: ajustarContraste(base, ['#FFFFFF'], CONTRASTE_TEXTO, 'oscurecer'),
  };
}
